export interface CalculatorInputs {
  distDate: string
  amount: string
  applyTrueUps: boolean
}

/** Parse a currency input string; returns null when invalid. */
export function parseDistributionAmount(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) {
    return null
  }
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null
  }
  return parsed
}

/** Format a number as USD currency for display in inputs ($#,##0.00). */
export function formatCurrencyField(value: number): string {
  if (value === 0) {
    return ''
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Format a dollar amount for clipboard (no symbol or grouping; keeps cents). */
export function formatAmountForClipboard(value: number): string {
  return value.toFixed(2)
}

export function inputsMatchSnapshot(
  current: CalculatorInputs,
  snapshot: CalculatorInputs | null,
): boolean {
  if (!snapshot) {
    return false
  }
  return (
    current.distDate === snapshot.distDate &&
    current.amount === snapshot.amount &&
    current.applyTrueUps === snapshot.applyTrueUps
  )
}

export function todayIsoDate(): string {
  return todayIsoDateInTimeZone('America/Chicago')
}

export function todayIsoDateInTimeZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const year = parts.find((p) => p.type === 'year')?.value ?? '1970'
  const month = parts.find((p) => p.type === 'month')?.value ?? '01'
  const day = parts.find((p) => p.type === 'day')?.value ?? '01'
  return `${year}-${month}-${day}`
}

export function inputsToApiMode(applyTrueUps: boolean): 'sheet' | 'no_trueups' {
  return applyTrueUps ? 'sheet' : 'no_trueups'
}

export function formatDisplayDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${m}/${d}/${y}`
}

/** Pre-fill Distribution Calculator amount from `?amount=` when no cached proposal exists. */
export function distributionAmountFromQuery(
  searchParams: URLSearchParams,
  cachedAmount: string | undefined | null,
): string | null {
  if (cachedAmount && cachedAmount.trim() !== '') {
    return null
  }
  const raw = searchParams.get('amount')
  if (!raw) {
    return null
  }
  const parsed = parseDistributionAmount(raw)
  if (parsed === null) {
    return null
  }
  return formatCurrencyField(parsed)
}
